import { useState, type CSSProperties, type KeyboardEvent } from "react";
import { Badge, Box, Group, Paper, Stack, Text, UnstyledButton } from "@mantine/core";
import { IconArrowRight, IconMapPin } from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import CountryFlag, { countryHasRenderableFlag } from "@/components/shared/CountryFlag";
import CorridorDoubleArrow from "@/components/shared/CorridorDoubleArrow";
import EcosystemHeadShortlistBookmarkButton from "@/pages/ecosystem-heads/components/EcosystemHeadShortlistBookmarkButton";
import {
  ECOSYSTEM_EXPLORE_CARD_HEADER_HEIGHT,
  ECOSYSTEM_EXPLORE_CARD_HEIGHT,
  ECOSYSTEM_EXPLORE_CARD_WIDTH,
} from "@/pages/ecosystem-heads/components/ecosystemCorridorPromo.util";
import { getCountryDisplayLabelFromHeadquarters } from "@shared/worldCountryIsoCodes";
import type { EcosystemHeadProfileStatus } from "@shared/ecosystemHeadProfile";
import placeholderClasses from "@/pages/ecosystem-heads/components/EcosystemCorridorPlaceholderCard.module.css";

export type EcosystemHeadCorridorCardData = {
  id: string;
  name: string;
  corridorName?: string;
  ecosystemIndustry: string | null;
  ecosystemCountry: string | null;
  ecosystemTargetCountry: string | null;
  corridorLabel: string;
  ecosystemHeadSummary?: string | null;
  ecosystemHeadLiveFrom?: string | null;
  profileStatus?: EcosystemHeadProfileStatus;
  expertiseTags?: string[];
  linkStatus?: string | null;
};

type EcosystemHeadCorridorCardProps = {
  head: EcosystemHeadCorridorCardData;
  isSelected?: boolean;
  fullWidth?: boolean;
  /** Navigate via router link (sector carousel, direct profile links). */
  href?: string;
  linkState?: unknown;
  /** Open drawer / custom handler (explore & sector list pages). */
  onClick?: () => void;
  ctaLabel?: string;
  isShortlisted?: boolean;
  showShortlistButton?: boolean;
  onShortlistClick?: () => void | Promise<void>;
};

const CARD_NAVY = "#04305c";
const CARD_BLUE = "#2563EB";
const CARD_BORDER = "#BFDBFE";

function formatLocationLabel(country: string | null | undefined): string | null {
  if (!country?.trim()) return null;
  return getCountryDisplayLabelFromHeadquarters(country) ?? country;
}

function formatDisplayName(value: string): string {
  return value
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function HeaderFlag({ country }: { country: string }) {
  return (
    <Box
      px={10}
      py={8}
      style={{
        borderRadius: 10,
        backgroundColor: "#FFFFFF",
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
        lineHeight: 0,
      }}
    >
      <Box style={{ transform: "scale(1.25)", transformOrigin: "center" }}>
        <CountryFlag country={country} />
      </Box>
    </Box>
  );
}

function HeaderCountryName({ label }: { label: string }) {
  return (
    <Box
      px={12}
      py={8}
      style={{
        borderRadius: 10,
        backgroundColor: "#FFFFFF",
        border: `1px solid ${CARD_BORDER}`,
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
        maxWidth: 120,
      }}
    >
      <Text size="xs" fw={700} c={CARD_NAVY} ta="center" lh={1.2}>
        {label}
      </Text>
    </Box>
  );
}

function renderCorridorHeaderCountries(
  originCountry: string | null | undefined,
  targetCountry: string | null | undefined,
) {
  const origin = originCountry?.trim() || null;
  const target = targetCountry?.trim() || null;

  if (origin && target) {
    const showFlags = countryHasRenderableFlag(origin) && countryHasRenderableFlag(target);
    if (showFlags) {
      return (
        <Group gap="sm" justify="center" wrap="nowrap" align="center">
          <HeaderFlag country={origin} />
          <CorridorDoubleArrow color={CARD_BLUE} />
          <HeaderFlag country={target} />
        </Group>
      );
    }

    const originLabel = formatLocationLabel(origin) ?? origin;
    const targetLabel = formatLocationLabel(target) ?? target;
    return (
      <Group gap="sm" justify="center" wrap="nowrap" align="center">
        <HeaderCountryName label={originLabel} />
        <CorridorDoubleArrow color={CARD_BLUE} />
        <HeaderCountryName label={targetLabel} />
      </Group>
    );
  }

  if (!origin) return null;

  if (countryHasRenderableFlag(origin)) {
    return (
      <Group gap="sm" justify="center" wrap="nowrap" align="center">
        <HeaderFlag country={origin} />
      </Group>
    );
  }

  return (
    <Group gap="sm" justify="center" wrap="nowrap" align="center">
      <HeaderCountryName label={formatLocationLabel(origin) ?? origin} />
    </Group>
  );
}

export default function EcosystemHeadCorridorCard({
  head,
  isSelected = false,
  fullWidth = false,
  href,
  linkState,
  onClick,
  ctaLabel = "View Profile",
  isShortlisted = false,
  showShortlistButton = false,
  onShortlistClick,
}: EcosystemHeadCorridorCardProps) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const hasTargetCountry = Boolean(head.ecosystemTargetCountry?.trim());
  const corridorName = head.corridorName?.trim() || head.corridorLabel;
  const locationLabel = formatLocationLabel(
    hasTargetCountry ? head.ecosystemTargetCountry : head.ecosystemCountry,
  );
  const headName = formatDisplayName(head.name);
  const isComingSoon = head.profileStatus === "COMING_SOON";
  const isActive = isHovered || isSelected;
  const isLink = Boolean(href);
  const showBookmark = !isComingSoon && showShortlistButton && Boolean(onShortlistClick);

  const viewProfileButton = isComingSoon ? (
    <Box
      py={9}
      className={placeholderClasses.footerButton}
      style={{
        flex: 1,
        borderRadius: 8,
        textAlign: "center",
        fontWeight: 700,
        fontSize: 13,
        minWidth: 0,
        pointerEvents: "none",
      }}
    >
      Coming Soon
    </Box>
  ) : (
    <Box
      py={9}
      style={{
        flex: 1,
        borderRadius: 8,
        textAlign: "center",
        fontWeight: 700,
        fontSize: 13,
        border: `1.5px solid ${CARD_NAVY}`,
        backgroundColor: isActive ? CARD_NAVY : "transparent",
        color: isActive ? "#FFFFFF" : CARD_NAVY,
        transition: "all 0.2s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        minWidth: 0,
      }}
    >
      {ctaLabel}
      <IconArrowRight size={15} stroke={2.25} />
    </Box>
  );

  const ctaRow = (
    <Group gap={8} wrap="nowrap" align="stretch" w="100%">
      {viewProfileButton}
      {showBookmark ? (
        <EcosystemHeadShortlistBookmarkButton
          isShortlisted={isShortlisted}
          onClick={() => onShortlistClick?.()}
        />
      ) : null}
    </Group>
  );

  const wrapperStyle: CSSProperties = {
    flex: fullWidth ? "1 1 auto" : "0 0 auto",
    width: fullWidth ? "100%" : ECOSYSTEM_EXPLORE_CARD_WIDTH,
    maxWidth: "100%",
    height: ECOSYSTEM_EXPLORE_CARD_HEIGHT,
    textAlign: "left",
    cursor: "pointer",
    display: "block",
  };

  const cardBody = (
      <Paper
        radius="lg"
        shadow="none"
        h="100%"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          display: "flex",
          flexDirection: "column",
          border: `1px solid ${isSelected ? "#60A5FA" : "#E2E8F0"}`,
          backgroundColor: "#FFFFFF",
          overflow: "hidden",
          boxShadow: isActive
            ? "0 16px 40px rgba(4, 48, 92, 0.18)"
            : "0 4px 20px rgba(15, 23, 42, 0.08)",
          transition: "box-shadow 0.25s ease, border-color 0.25s ease",
        }}
      >
        <Box
          h={ECOSYSTEM_EXPLORE_CARD_HEADER_HEIGHT}
          px="md"
          style={{
            backgroundColor: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {renderCorridorHeaderCountries(head.ecosystemCountry, head.ecosystemTargetCountry)}
        </Box>

        <Stack gap={4} p="md" pt={10} style={{ flex: 1, minHeight: 0 }}>
          <Text fw={800} size="xl" c={CARD_NAVY} lh={1.2} ta="center">
            {corridorName}
          </Text>

          {locationLabel ? (
            <Group gap={5} wrap="nowrap" align="center">
              <IconMapPin size={14} color={CARD_BLUE} style={{ flexShrink: 0 }} />
              <Text size="xs" c={CARD_BLUE} fw={500}>
                {locationLabel}
              </Text>
            </Group>
          ) : null}

          {head.ecosystemHeadLiveFrom?.trim() ? (
            <Badge color="green" variant="light" size="sm" w="fit-content">
              Live from {head.ecosystemHeadLiveFrom.trim()}
            </Badge>
          ) : null}

          <Stack gap={8} mt="auto" style={{ flexShrink: 0 }}>
            <Text c={CARD_NAVY}>Led By : {headName}</Text>

            {ctaRow}
          </Stack>
        </Stack>
      </Paper>
  );

  const isInteractive = Boolean(href || onClick);

  const handleCardActivate = () => {
    if (href) {
      navigate(href, { state: linkState });
      return;
    }
    onClick?.();
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleCardActivate();
  };

  if (showBookmark && isInteractive) {
    return (
      <Box
        style={wrapperStyle}
        onClick={handleCardActivate}
        onKeyDown={handleCardKeyDown}
        role="button"
        tabIndex={0}
      >
        {cardBody}
      </Box>
    );
  }

  if (isLink) {
    return (
      <UnstyledButton component={Link} to={href!} state={linkState} style={wrapperStyle}>
        {cardBody}
      </UnstyledButton>
    );
  }

  return (
    <UnstyledButton onClick={onClick} style={wrapperStyle}>
      {cardBody}
    </UnstyledButton>
  );
}
